import React, { useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import moment from "moment";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import CustomTable from "../../../components/table/table";
import {
  adminInternshipsAbsorbedList,
  superadminInternshipsAbsorbedList,
} from "../admin.api";
import "./style.css";
import * as XLSX from "xlsx";
import { FiEye, FiFileText } from "react-icons/fi";


export default function InternshipsAbsorbedList(props) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);

  let adminType = Number(jwtDecode(props.state.payload).user_type);

  const fetchInternshipsAbsorbed = () => {
    setIsLoading(true);
    const apiCall =
      adminType === 2
        ? superadminInternshipsAbsorbedList(page, perPage)
        : adminInternshipsAbsorbedList(page, perPage);

    apiCall
      .then((res) => {
        if (res?.success) {
          let tdata = res.data || [];
          tdata.forEach((item, index) => {
            item["slno"] = perPage === "all" ? index + 1 : (page - 1) * perPage + index + 1;
          });
          setData(tdata);
          setTotalPages(res.pagination?.last_page || 1);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching internships absorbed:", error);
        setSnackMessage("Failed to load absorbed internships directory");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchInternshipsAbsorbed();
  }, [page, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    setPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(
        <button key={1} className="btn btn-sm btn-outline-primary" onClick={() => setPage(1)}>1</button>
      );
      if (start > 2) pages.push(<span key="start-dots" className="px-1 text-primary">...</span>);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${page === i ? "btn-primary text-white" : "btn-outline-primary"}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="end-dots" className="px-1 text-primary">...</span>);
      pages.push(
        <button key={totalPages} className="btn btn-sm btn-outline-primary" onClick={() => setPage(totalPages)}>{totalPages}</button>
      );
    }

    return pages;
  };

  const handleView = (row) => {
    let path = `/admin-internship-requests/${row.internship_request_id}`;
    props.history.push(path);
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  async function fetchExportData() {
    const exportdata = [];
    data.forEach((item, index) => {
      exportdata.push({
        "Sl No": index + 1,
        "Intern Name": item.internship?.full_name || "--",
        "Company": item.internship_request?.company?.company_name || "--",
        "Discipline": item.internship_request?.internship_discipline || "--",
        "Absorption Date": item.updated_at ? moment(item.updated_at).format("DD-MMM-YYYY") : "--",
      });
    });
    return exportdata;
  }

  const downloadExcel = async () => {
    setIsDownloading(true);
    try {
      // Small delay to ensure loading state is visible
      await new Promise(resolve => setTimeout(resolve, 500));
      const exportData = await fetchExportData();

      if (!exportData.length) {
        setSnackMessage("No data available to download");
        setIsSnackError(true);
        setSnackOpen(true);
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Internships Absorbed");

      XLSX.writeFile(workbook, "Internship_Absorbed_Directory.xlsx");
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="container-fluid mt-4">
      <Loader open={isLoading} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><b>Directory of Internships Absorbed</b></h5>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={downloadExcel} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <svg className="me-2" style={{ width: "1.2rem", height: "1.2rem", animation: "spin 1s linear infinite" }} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
                    <path d="M12 2C6.48 2 2 6.48 2 12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                  Downloading...
                </>
              ) : (
                "Download Excel"
              )}
            </button>
          </div>
        </div>

        <div className="card-body pt-0">
          <CustomTable
            {...props}
            columns={[
              {
                title: "Intern Name",
                field: "internship",
                render: (row) => (
                  <div>
                    <div className="fw-bold">{row.internship?.full_name || "--"}</div>
                    <div className="x-small text-muted">{row.internship?.email}</div>
                  </div>
                ),
              },
              {
                title: "Company",
                field: "company",
                render: (row) => row.internship_request?.company?.company_name || "--",
              },
              {
                title: "Discipline",
                render: (row) => row.internship_request?.internship_discipline || "--"
              },
              {
                title: "Absorbed On",
                render: (row) => (
                  <div className="small">
                    {row.updated_at ? moment(row.updated_at).format("DD-MMM-YYYY") : "--"}
                  </div>
                ),
              },
              {
                title: "Action",
                render: (row) => (
                  <div className="d-flex gap-1">
                    <button
                      className="btn btn-sm btn-outline-primary action-btn"
                      onClick={() => handleView(row)}
                      title="View Request"
                    >
                      <FiEye size={16} />
                    </button>
                    {row.joint_certificate && (
                      <a
                        href={row.joint_certificate.startsWith('http') ? row.joint_certificate : `${(process.env.REACT_APP_BASE_URL || "").replace(/\/api\/?$/, "")}/storage/${row.joint_certificate}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-success action-btn"
                        title="Certificate"
                      >
                        <FiFileText size={16} />
                      </a>
                    )}
                  </div>
                ),
              },
            ]}
            data={data}
            paging={false}
            options={{
              search: true,
              selection: false,
              exportButton: false,
            }}
          />

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div className="flex-grow-1"></div>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              {renderPagination()}
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
            <div className="flex-grow-1 d-flex justify-content-end">
              <select
                className="form-control form-control-sm w-auto ml-2"
                value={perPage}
                onChange={handlePerPageChange}
                style={{ fontSize: "8px", height: "31px" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
